import { useState } from "react";
import {
    Title, Tabs, Group, Button, Table, ActionIcon, Stack, Text, Card, Menu,
} from "@mantine/core";
import { IconPlus, IconDots, IconPencil, IconTrash, IconHistory, IconEye } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import type { Level } from "@iso27001/shared";
import { likelihoodLevelsApi, damageLevelsApi } from "../../api/levels.js";
import { LevelFormModal } from "./LevelFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen } from "../../components/list-table.js";

interface SectionProps {
    api: typeof likelihoodLevelsApi;
    queryKey: readonly unknown[];
    auditQueryKey: (id: number) => readonly unknown[];
}

function LevelsSection({ api, queryKey, auditQueryKey }: SectionProps) {
    const { user } = useAuth();
    const canEdit = user?.role === "admin" || user?.role === "editor";
    const qc = useQueryClient();
    const { t } = useTranslation();
    const [auditId, setAuditId] = useState<number | null>(null);
    const dialog = useRecordDialog<Level>();

    const list = useQuery({ queryKey, queryFn: api.list });
    const removeMut = useMutation({
        mutationFn: (id: number) => api.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey });
            notifications.show({ message: t("levels.list.notify.deleted"), color: "teal" });
        },
    });
    const onDelete = (l: Level) =>
        modals.openConfirmModal({
            title: t("levels.list.confirmDelete.title"),
            children: <Text size="sm">{t("levels.list.confirmDelete.message", { label: l.label })}</Text>,
            labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
            confirmProps: { color: "red" },
            onConfirm: () => removeMut.mutate(l.id),
        });

    return (
        <Stack mt="md">
            <Group justify="space-between">
                <Text size="sm" c="dimmed">{t("levels.list.count", { count: list.data?.total ?? 0 })}</Text>
                {canEdit && <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>{t("levels.newLevel")}</Button>}
            </Group>
            <Card withBorder padding={0}>
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ width: 80 }}>{t("levels.list.column.sort")}</Table.Th>
                            <Table.Th style={{ width: 80 }}>{t("levels.list.column.value")}</Table.Th>
                            <Table.Th>{t("common.field.code")}</Table.Th>
                            <Table.Th>{t("levels.field.label")}</Table.Th>
                            <Table.Th style={{ width: 48 }} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {list.data?.items.map((l) => (
                            <Table.Tr key={l.id} {...rowOpenProps(() => dialog.open(l))}>
                                <Table.Td>{l.sortOrder}</Table.Td>
                                <Table.Td>{l.numericValue}</Table.Td>
                                <Table.Td><Text ff="monospace" size="sm">{l.code}</Text></Table.Td>
                                <Table.Td>{l.label}</Table.Td>
                                <Table.Td {...stopRowOpen}>
                                    <Menu position="bottom-end">
                                        <Menu.Target><ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon></Menu.Target>
                                        <Menu.Dropdown>
                                            {canEdit
                                                ? <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(l)}>{t("common.action.edit")}</Menu.Item>
                                                : <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(l)}>{t("common.action.open")}</Menu.Item>}
                                            <Menu.Item leftSection={<IconHistory size={14} />} onClick={() => setAuditId(l.id)}>{t("common.action.history")}</Menu.Item>
                                            {canEdit && <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(l)}>{t("common.action.delete")}</Menu.Item>}
                                        </Menu.Dropdown>
                                    </Menu>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                        {list.data?.items.length === 0 && (
                            <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="md">{t("levels.list.empty")}</Text></Table.Td></Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </Card>
            <LevelFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} api={api} queryKey={queryKey} />
            <AuditHistoryModal
                opened={auditId !== null}
                onClose={() => setAuditId(null)}
                queryKey={auditQueryKey(auditId ?? 0)}
                fetchFn={() => api.audit(auditId!)}
            />
        </Stack>
    );
}

export function LevelsPage() {
    const { t } = useTranslation();
    return (
        <Stack>
            <Title order={2}>{t("levels.title")}</Title>
            <Tabs defaultValue="likelihood">
                <Tabs.List>
                    <Tabs.Tab value="likelihood">{t("levels.tab.likelihood")}</Tabs.Tab>
                    <Tabs.Tab value="damage">{t("levels.tab.damage")}</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="likelihood">
                    <LevelsSection
                        api={likelihoodLevelsApi}
                        queryKey={["likelihood-levels"]}
                        auditQueryKey={(id) => ["likelihood-levels", "audit", id]}
                    />
                </Tabs.Panel>
                <Tabs.Panel value="damage">
                    <LevelsSection
                        api={damageLevelsApi}
                        queryKey={["damage-levels"]}
                        auditQueryKey={(id) => ["damage-levels", "audit", id]}
                    />
                </Tabs.Panel>
            </Tabs>
        </Stack>
    );
}
